import { valibotResolver } from "@hookform/resolvers/valibot";
import { useEffect, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";

import useInboxDraftStore from "common/hooks/useInboxDraftStore";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";
import useConversationUpdates from "../../../datahooks/useConversationUpdates";
import { useNewConversation } from "../../../hooks/useNewConversation";
import {
  InboxMessageSchema,
  type InboxMessageSchemaType,
} from "../../../schemas";
import MessageField from "./MessageField";

import type { ConversationMessage } from "../../../types";

interface MessageContainerProps {
  editMessage: ConversationMessage;
  hasMessageFromAnotherUser: boolean;
  cancelEdit: () => void;
  scrollToBottom: () => void;
}

export default function MessageContainer({
  editMessage,
  hasMessageFromAnotherUser,
  cancelEdit,
  scrollToBottom,
}: MessageContainerProps) {
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();

  const formReturn = useForm<InboxMessageSchemaType>({
    resolver: valibotResolver(InboxMessageSchema),
    defaultValues: {
      message: "",
    },
  });
  const { handleSubmit, reset, subscribe } = formReturn;
  const { sendMessage, isSendingMessage } = useConversationUpdates();
  const { updateMessage, isUpdatingMessage } = useConversationMessagesUpdates({
    conversationUrn: activeConversation
      ? activeConversation.conversation_urn
      : "",
  });
  const { drafts, setDraft } = useInboxDraftStore();

  const draftKey = activeConversation
    ? `${activeConversation.conversation_urn}:${activeConversation.owner}`
    : `new:${newConversation.participant.id}:${newConversation.owner}`;

  useLayoutEffect(() => {
    if (activeConversation) {
      reset({ message: drafts[draftKey] || "" });
    }
  }, [activeConversation]);

  useEffect(() => {
    return subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        if ("message" in values && !editMessage) {
          setDraft(draftKey, values.message);
        }
      },
    });
  }, [subscribe, editMessage]);

  function onSubmit(data: InboxMessageSchemaType) {
    if (isSendingMessage || isUpdatingMessage) {
      return;
    }
    reset({ message: "" });

    //editing action
    if (editMessage && "message" in data) {
      const { message } = data;
      if (message.trim() !== editMessage.body) {
        updateMessage({
          accountId: activeConversation.owner,
          message,
          messageUrn: editMessage.urn,
        }).then(() => {
          cancelEdit();
        });
      }
    } else {
      sendMessage(
        activeConversation
          ? {
              accountId: activeConversation.owner,
              recipients: activeConversation.participants,
              conversationUrn: activeConversation.conversation_urn,
              data,
            }
          : {
              accountId: newConversation.owner,
              recipients: [newConversation.participant],
              data,
            },
      ).then(() => {
        scrollToBottom();
      });
    }
  }
  return (
    <MessageField
      editMessage={editMessage}
      cancelEdit={cancelEdit}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isSendingMessage || isUpdatingMessage}
      formReturn={formReturn}
      hasMessageFromAnotherUser={hasMessageFromAnotherUser}
    />
  );
}
